import moment from 'moment'
import {
  ConsentFormDataMock,
  ConsentFormIncludedOrder,
  ConsentFormIncludedProcedure,
  ConsentFormIncludedStaff,
  ConsentFormResMock
} from '../types/consent_form.type'
import { GeneralResMock } from '../types/general.type'
import { MedicationDataMock, MedicationIncluded } from '../types/medication.type'
import { VitalDataMock } from '../types/vital.type'
import { capitalizeDrugName } from './nameSplit'

// Generic helper to index JSON:API included array by type -> id
export function buildIncludedIndex<T extends { id: string; type: string }>(
  included: T[] | undefined
): Record<string, Record<string, T>> {
  const index: Record<string, Record<string, T>> = {}
  ;(included || []).forEach(item => {
    if (!index[item.type]) index[item.type] = {}
    index[item.type][item.id] = item
  })
  return index
}

export function transformLabComponentTable(apiResponse: any) {
  const includedMap = new Map<string, any>()
  apiResponse?.included?.forEach((item: any) => {
    includedMap.set(`${item.type}-${item.id}`, item)
  })

  const dates: Set<string> = new Set()
  const tableRows: any[] = []

  apiResponse?.data?.forEach((labResult: any) => {
    const orderRef = labResult.relationships.order.data
    const order = includedMap.get(`${orderRef.type}-${orderRef.id}`)

    let labTestName = ''
    if (order?.relationships?.orderable?.data) {
      const labTestRef = order.relationships.orderable.data
      const labTest = includedMap.get(`${labTestRef.type}-${labTestRef.id}`)
      labTestName = labTest?.attributes?.name || labTest?.attributes?.study_type || ''
    }

    const date = labResult.attributes.updated_at.split('T')[0]
    dates.add(date)

    if (!labResult.attributes.components?.length) {
      // Single interpretation row
      tableRows.push({
        component: labTestName,
        [date]: labResult.attributes.interpretation || '',
        name: labTestName
      })
    } else {
      // Multiple components
      labResult.attributes.components.forEach((comp: any) => {
        tableRows.push({
          component: comp.name,
          [date]: comp.value || '',
          name: labTestName
        })
      })
    }
  })

  // Merge rows with same name + component into single object with multiple date keys
  const mergedRows: Record<string, any> = {}
  tableRows.forEach(row => {
    const key = `${row.name}||${row.component}`
    if (!mergedRows[key]) {
      mergedRows[key] = { component: row.component, name: row.name }
    }
    Object.keys(row).forEach(k => {
      if (k !== 'name' && k !== 'component') {
        mergedRows[key][k] = row[k]
      }
    })
  })

  return {
    dates: Array.from(dates).sort(),
    table: Object.values(mergedRows)
  }
}

export function transformVitalsForTable(apiResponse: VitalDataMock[]) {
  const vitals = apiResponse
  const dateSet = new Set<string>()
  const vitalsMap = new Map()

  vitals.forEach(item => {
    const { vital_type, value, updated_at, unit } = item.attributes

    // Format date for table header
    const formattedDate = moment(updated_at).format('DD/MM/YYYY')
    dateSet.add(formattedDate)

    // Clean vital type name (remove LOINC code if present)
    const cleanType = vital_type.replace(/\s*\(LOINC:[^)]+\)/, '').trim()
    const displayName = `${cleanType} (${unit})`

    if (!vitalsMap.has(displayName)) {
      vitalsMap.set(displayName, {})
    }

    vitalsMap.get(displayName)[formattedDate] = value
  })

  const sortedDates = Array.from(dateSet).sort(
    (a, b) => moment(b, 'DD/MM/YYYY').valueOf() - moment(a, 'DD/MM/YYYY').valueOf()
  )

  // Build final tableData object
  const tableData = {
    headers: ['Parameters', ...sortedDates],
    rows: Array.from(vitalsMap.entries())?.map(([parameter, valuesByDate]) => {
      const values = sortedDates?.map(date => valuesByDate[date] || '')
      return { parameter, values }
    })
  }

  return tableData
}
export function extractMedications(data: GeneralResMock<MedicationDataMock[], MedicationIncluded[]>) {
  const includedMap = buildIncludedIndex<MedicationIncluded>(data.included)
  return data.data?.map(medication => {
    const drugId = medication.relationships?.product?.data?.id
    const frequencyId = medication.relationships?.medication_frequency?.data?.id

    return {
      dosage: medication.attributes.dosage,
      drugName: capitalizeDrugName(
        medication.attributes.nf_drug_name || includedMap['product']?.[drugId]?.attributes?.brand_name
      ),
      frequencyCode: includedMap['medication_frequency']?.[frequencyId]?.attributes?.code ?? null,
      route: medication.attributes.route
    }
  })
}

export function extractProcedure(data: ConsentFormResMock<ConsentFormDataMock[]>) {
  return data.data?.map(item => {
    // find attending doctor id
    const attendingDoctorId = item.relationships.attending_doctor?.data?.id

    // find doctor in included
    const doctor = data.included.find(
      inc => inc.type === 'staff' && inc.id === attendingDoctorId
    ) as ConsentFormIncludedStaff

    const doctorName = doctor ? `${doctor.attributes.first_name} ${doctor.attributes.last_name}` : 'Unknown'

    // find procedure id from order relationship
    const orderId = item.relationships.order?.data?.id
    const order = data.included.find(inc => inc.type === 'order' && inc.id === orderId) as ConsentFormIncludedOrder

    const procedureId = order?.relationships?.orderable?.data?.id

    const procedure = data.included.find(
      inc => inc.type === 'procedure' && inc.id === procedureId
    ) as ConsentFormIncludedProcedure

    const procedureName = procedure ? procedure.attributes.name : 'Unknown'

    return {
      doctor: doctorName,
      name: procedureName
    }
  })
}
